import { create } from "zustand"

import {
  GetGoodsReceiptListDocument,
  type GetGoodsReceiptListQuery,
  type GoodsReceipt,
  type GoodsReceiptSortField,
  type GoodsReceiptStatus,
  type PageInfo,
  type SortingOrder,
} from "@/graphql/codegen/graphql"
import { type SearchFilters, type SearchStore, createSearchStore } from "@/stores/createSearchStore"

export type GoodsReceiptsSearchFilters = SearchFilters & {
  warehouseAreaIds?: string[]
  statuses?: GoodsReceiptStatus[]
  field?: GoodsReceiptSortField
  order?: SortingOrder
}

type ExtendedGoodsReceiptsSearchStore = SearchStore<GoodsReceipt, GoodsReceiptsSearchFilters> & {
  pageInfo?: PageInfo
}

const deduplicateGoodsReceipts = (newData: GoodsReceipt[], existingData: GoodsReceipt[]): GoodsReceipt[] => {
  const uniqueIds = new Set(existingData.map((receipt) => receipt.goodsReceiptId))
  return [...existingData, ...newData.filter((receipt) => !uniqueIds.has(receipt.goodsReceiptId))]
}

export const useGoodsReceiptsSearchStore = create<ExtendedGoodsReceiptsSearchStore>((set, get, store) => {
  const baseStoreCreator = createSearchStore<GoodsReceipt, GoodsReceiptsSearchFilters, GetGoodsReceiptListQuery>({
    documentNode: GetGoodsReceiptListDocument,
    deduplicateData: deduplicateGoodsReceipts,
    getQueryInput: (filters: GoodsReceiptsSearchFilters, offset: number, limit: number) => {
      const { field, order, warehouseAreaIds, statuses, ...otherFilters } = filters
      return {
        filter: {
          ...otherFilters,
          warehouseIds: warehouseAreaIds,
          status: statuses,
        },
        offset,
        limit,
        sort: field && order ? { field, order } : undefined,
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => {
      const pageInfo =
        data.goodsReceipt.list.__typename === "GoodsReceiptListSuccess" ? data.goodsReceipt.list.pageInfo : undefined

      set({ pageInfo })

      return {
        __typename: data.goodsReceipt.list.__typename,
        data:
          data.goodsReceipt.list.__typename === "GoodsReceiptListSuccess"
            ? (data.goodsReceipt.list.goodsReceipts as GoodsReceipt[])
            : [],
        error:
          data.goodsReceipt.list.__typename === "GoodsReceiptListFailure" ? data.goodsReceipt.list.error : undefined,
      }
    },
  })

  const baseStore = baseStoreCreator(set, get, store)

  return {
    ...baseStore,
    pageInfo: undefined,
  }
})
